// src/pages/ShopForemanDashboard.js
import React, { useCallback, useMemo, useState } from "react";
import { useROStore } from "../context/ROContext";
import { useAuth } from "../context/AuthContext";
import { useROUnread } from "../context/ROUnreadContext";
import ROActivityDrawer from "../components/ro/ROActivityDrawer";
import {
  normalizeROArray,
  isWaitingOnPartsLike,
  getCreatedISO,
  getAssignedISO,
} from "../utils/metricsEngine";

const DISPATCH_WARN_MIN = 30;
const PARTS_WARN_MIN = 60;

/* -----------------------------
   Safe fallbacks
----------------------------- */
const FALLBACK_RO_STATUS = Object.freeze({
  DISPATCH: "DISPATCH",
  ASSIGNED: "ASSIGNED",
  IN_PROGRESS: "IN_PROGRESS",
  WAITING_ON_PARTS: "WAITING_ON_PARTS",
  COMPLETED: "COMPLETED",
  ARCHIVED: "ARCHIVED",
});

const FALLBACK_RO_LIFECYCLE = Object.freeze({
  OPEN: "OPEN",
  WAITING_FOR_PARTS: "WAITING_FOR_PARTS",
  WAITING_FOR_ASSIGNMENT: "WAITING_FOR_ASSIGNMENT",
  IN_PROGRESS: "IN_PROGRESS",
  COMPLETED: "COMPLETED",
});

function safeString(v) {
  return typeof v === "string" ? v : v == null ? "" : String(v);
}

function safeISO(v) {
  if (!v) return "";
  const s = String(v).trim();
  if (!s) return "";
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? "" : s;
}

function minutesSince(startIso, endIso) {
  const a = safeISO(startIso);
  if (!a) return null;
  const start = new Date(a);
  const end = endIso ? new Date(endIso) : new Date();
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  const diffMs = end.getTime() - start.getTime();
  if (diffMs < 0) return 0;
  return diffMs / 1000 / 60;
}

function formatMinutesCompact(mins) {
  if (mins == null) return "—";
  const rounded = Math.round(mins);
  if (rounded < 60) return `${rounded}m`;
  const h = Math.floor(rounded / 60);
  const m = rounded % 60;
  if (h < 24) return m ? `${h}h ${m}m` : `${h}h`;
  const d = Math.floor(h / 24);
  const rh = h % 24;
  return rh ? `${d}d ${rh}h` : `${d}d`;
}

function transportLabel(v) {
  const s = String(v || "").toUpperCase();
  if (s === "WAITER") return "Waiter";
  if (s === "LOANER") return "Loaner";
  if (s === "SHUTTLE") return "Shuttle";
  if (s === "OTHER") return "Other";
  return "";
}

function workMixLabel(v) {
  const s = String(v || "").toUpperCase();
  if (s === "MAINTENANCE") return "Maintenance";
  if (s === "DIAGNOSIS") return "Diagnosis";
  if (s === "OTHER") return "Other";
  return "Other";
}

function transportTone(v) {
  const s = String(v || "").toUpperCase();
  if (s === "WAITER") return "waiter";
  if (s === "LOANER") return "loaner";
  if (s === "SHUTTLE") return "shuttle";
  return "other";
}

function fmtPromise(iso) {
  const t = safeISO(iso);
  if (!t) return "";
  const d = new Date(t);
  return d.toLocaleString();
}

const DATE_FILTERS = Object.freeze({
  TODAY: "TODAY",
  YESTERDAY: "YESTERDAY",
  THIS_WEEK: "THIS_WEEK",
  THIS_MONTH: "THIS_MONTH",
  CUSTOM: "CUSTOM",
});

function startOfLocalDay(input = new Date()) {
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function addDays(input, days) {
  const d = input instanceof Date ? new Date(input) : new Date(input);
  d.setDate(d.getDate() + days);
  return d;
}

function startOfLocalWeekMonday(input = new Date()) {
  const d = startOfLocalDay(input);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  return addDays(d, diff);
}

function dateInputValue(input = new Date()) {
  const d = input instanceof Date ? input : new Date(input);
  const valid = Number.isNaN(d.getTime()) ? new Date() : d;
  const pad = (n) => String(n).padStart(2, "0");
  return `${valid.getFullYear()}-${pad(valid.getMonth() + 1)}-${pad(valid.getDate())}`;
}

function localDateFromInput(value) {
  const s = safeString(value).trim();
  if (!s) return null;
  const [year, month, day] = s.split("-").map((part) => Number(part));
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

function getForemanDateWindow(filter, customFrom, customTo) {
  const todayStart = startOfLocalDay();
  const tomorrowStart = addDays(todayStart, 1);

  if (filter === DATE_FILTERS.YESTERDAY) {
    return {
      start: addDays(todayStart, -1),
      end: todayStart,
      label: "Yesterday",
      includeCarryoverActive: false,
    };
  }

  if (filter === DATE_FILTERS.THIS_WEEK) {
    return {
      start: startOfLocalWeekMonday(),
      end: tomorrowStart,
      label: "This Week",
      includeCarryoverActive: false,
    };
  }

  if (filter === DATE_FILTERS.THIS_MONTH) {
    const now = new Date();
    return {
      start: new Date(now.getFullYear(), now.getMonth(), 1),
      end: tomorrowStart,
      label: "This Month",
      includeCarryoverActive: false,
    };
  }

  if (filter === DATE_FILTERS.CUSTOM) {
    const start = localDateFromInput(customFrom);
    const toStart = localDateFromInput(customTo);
    const end = toStart ? addDays(toStart, 1) : null;

    return {
      start,
      end,
      label: customFrom && customTo ? `${customFrom} to ${customTo}` : "Custom Range",
      includeCarryoverActive: false,
    };
  }

  return {
    start: todayStart,
    end: tomorrowStart,
    label: "Today",
    includeCarryoverActive: true,
  };
}

function isWithinDateWindow(input, dateWindow) {
  if (!dateWindow?.start || !dateWindow?.end) return true;

  const t = new Date(safeString(input).trim()).getTime();
  const start = dateWindow.start.getTime();
  const end = dateWindow.end.getTime();

  if (!Number.isFinite(t)) return false;

  return t >= start && t < end;
}

function getForemanDateAnchor(ro) {
  const lc = safeString(ro?.lifecycle || ro?.lifeCycle || "").toUpperCase();
  const st = safeString(ro?.status || "").toUpperCase();

  if (lc === "WAITING_FOR_PARTS" || lc === "WAITING_ON_PARTS" || st === "WAITING_FOR_PARTS" || st === "WAITING_ON_PARTS") {
    return (
      safeISO(ro?.timestamps?.waitingPartsAt) ||
      safeISO(ro?.waitingPartsAt) ||
      safeISO(ro?.waiting_parts_at) ||
      safeISO(ro?.partsActionRequestedAt) ||
      safeISO(ro?.timestamps?.partsRequestedAt) ||
      safeISO(getCreatedISO(ro)) ||
      safeISO(ro?.createdAt)
    );
  }

  if (lc === "IN_PROGRESS" || st === "IN_PROGRESS") {
    return (
      safeISO(ro?.timestamps?.workStartAt) ||
      safeISO(ro?.workStartAt) ||
      safeISO(ro?.timestamps?.activeWorkStartedAt) ||
      safeISO(ro?.activeWorkStartedAtISO) ||
      safeISO(ro?.startedAt) ||
      safeISO(getCreatedISO(ro)) ||
      safeISO(ro?.createdAt)
    );
  }

  if (lc === "OPEN" || st === "ASSIGNED" || isAssignedLike(ro)) {
    return (
      safeISO(getAssignedISO(ro)) ||
      safeISO(ro?.assignedAt) ||
      safeISO(ro?.timestamps?.assignedAt) ||
      safeISO(getCreatedISO(ro)) ||
      safeISO(ro?.createdAt)
    );
  }

  return safeISO(getCreatedISO(ro)) || safeISO(ro?.createdAt) || safeISO(ro?.updatedAt);
}

function isWithinForemanDateWindow(ro, dateWindow) {
  if (!dateWindow?.start || !dateWindow?.end) return true;
  if (dateWindow.includeCarryoverActive) return true;
  return isWithinDateWindow(getForemanDateAnchor(ro), dateWindow);
}

function isAssignedLike(ro) {
  return (
    !!safeString(ro?.assignedTechId).trim() ||
    !!safeString(ro?.assignedTechName).trim()
  );
}

function isArchivedLike(ro, RO_STATUS_ENUM) {
  const st = String(ro?.status || "").toUpperCase();
  return st === String(RO_STATUS_ENUM.ARCHIVED).toUpperCase();
}

function isCompletedLike(ro, RO_STATUS_ENUM, RO_LIFECYCLE_ENUM) {
  const lc = String(ro?.lifecycle || "").toUpperCase();
  if (lc) return lc === String(RO_LIFECYCLE_ENUM.COMPLETED).toUpperCase();
  const st = String(ro?.status || "").toUpperCase();
  return st === String(RO_STATUS_ENUM.COMPLETED).toUpperCase();
}

function isVehicleGoneLike(ro) {
  const status = safeString(ro?.status).toUpperCase();
  const lifecycle = safeString(ro?.lifecycle || ro?.lifeCycle).toUpperCase();

  const pickupStatus = safeString(
    ro?.customerPickupStatus || ro?.customer_pickup_status
  ).toUpperCase();

  const advisorAction = safeString(
    ro?.advisorActionStatus || ro?.advisor_action_status
  ).toUpperCase();

  return (
    status === "VEHICLE_GONE" ||
    status === "PICKED_UP" ||
    lifecycle === "VEHICLE_GONE" ||
    pickupStatus === "VEHICLE_GONE" ||
    advisorAction === "VEHICLE_GONE" ||
    !!ro?.vehicleGone ||
    !!ro?.vehicle_gone ||
    !!ro?.pickedUp ||
    !!ro?.picked_up ||
    !!ro?.pickedUpAt ||
    !!ro?.picked_up_at ||
    !!ro?.pickedUpAtISO ||
    !!ro?.picked_up_at_iso ||
    !!ro?.vehicleGoneAt ||
    !!ro?.vehicle_gone_at ||
    !!ro?.vehicleGoneAtISO ||
    !!ro?.vehicle_gone_at_iso ||
    !!ro?.timestamps?.vehicleGoneAt ||
    !!ro?.timestamps?.vehicle_gone_at ||
    !!ro?.timestamps?.pickedUpAt ||
    !!ro?.timestamps?.picked_up_at
  );
}

function lifecycleOf(ro) {
  return String(ro?.lifecycle || "").toUpperCase();
}

function statusOf(ro) {
  return String(ro?.status || "").toUpperCase();
}

function getTag(ro) {
  const raw =
    ro?.tagNumber ??
    ro?.tag_number ??
    ro?.tag ??
    ro?.keyTag ??
    ro?.key_tag ??
    ro?.tagId ??
    ro?.tag_id ??
    "";
  return safeString(raw).trim();
}

function latestSharedNote(notes) {
  const raw = safeString(notes).trim();
  if (!raw) return "";

  const blocks = raw
    .split(/\n\s*\n/)
    .map((part) => safeString(part).trim())
    .filter(Boolean);

  if (!blocks.length) return "";

  const latestBlock = blocks[blocks.length - 1];

  const lines = latestBlock
    .split("\n")
    .map((line) => safeString(line).trim())
    .filter(Boolean);

  if (lines.length <= 1) return lines[0] || "";

  return lines.slice(1).join(" ");
}

function pillBase() {
  return {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 5,
    padding: "2px 7px",
    borderRadius: 999,
    fontSize: 10,
    fontWeight: 800,
    border: "1px solid #e5e7eb",
    whiteSpace: "nowrap",
    lineHeight: 1.1,
  };
}

function tagPillStyle(hasTag) {
  const base = pillBase();
  if (!hasTag) {
    return {
      ...base,
      borderColor: "#fecaca",
      background: "#fef2f2",
      color: "#991b1b",
    };
  }
  return {
    ...base,
    borderColor: "#cbd5e1",
    background: "#f8fafc",
    color: "#0f172a",
  };
}

function tonePillStyle(kind) {
  const base = pillBase();

  if (kind === "waiter") {
    return {
      ...base,
      borderColor: "#fecaca",
      background: "#fef2f2",
      color: "#991b1b",
    };
  }

  if (kind === "loaner") {
    return {
      ...base,
      borderColor: "#bfdbfe",
      background: "#eff6ff",
      color: "#1d4ed8",
    };
  }

  if (kind === "shuttle") {
    return {
      ...base,
      borderColor: "#c7d2fe",
      background: "#eef2ff",
      color: "#3730a3",
    };
  }

  if (kind === "tech") {
    return {
      ...base,
      borderColor: "#cbd5e1",
      background: "#f8fafc",
      color: "#111827",
    };
  }

  if (kind === "assigned") {
    return {
      ...base,
      borderColor: "#cbd5e1",
      background: "#f8fafc",
      color: "#0f172a",
    };
  }

  if (kind === "inprogress") {
    return {
      ...base,
      borderColor: "#c7d2fe",
      background: "#eef2ff",
      color: "#3730a3",
    };
  }

  if (kind === "parts") {
    return {
      ...base,
      borderColor: "#fdba74",
      background: "#ffedd5",
      color: "#9a3412",
    };
  }

  if (kind === "stage") {
    return {
      ...base,
      borderColor: "#fecaca",
      background: "#fef2f2",
      color: "#991b1b",
    };
  }

  if (kind === "promise") {
    return {
      ...base,
      borderColor: "#fed7aa",
      background: "#fff7ed",
      color: "#9a3412",
    };
  }

  if (kind === "queue") {
    return {
      ...base,
      borderColor: "#86efac",
      background: "#dcfce7",
      color: "#166534",
    };
  }

  return {
    ...base,
    borderColor: "#cbd5e1",
    background: "#f8fafc",
    color: "#334155",
  };
}

function threadButtonStyle(hasUnreadNote) {
  return {
    position: "relative",
    padding: "6px 9px",
    borderRadius: 8,
    border: hasUnreadNote ? "1px solid #fdba74" : "1px solid #d1d5db",
    background: hasUnreadNote ? "#fff7ed" : "#ffffff",
    color: hasUnreadNote ? "#9a3412" : "#111827",
    cursor: "pointer",
    fontWeight: 800,
    fontSize: 11.5,
    whiteSpace: "nowrap",
    minHeight: 30,
  };
}

const pageStyle = {
  padding: 12,
  display: "grid",
  gap: 10,
  maxWidth: 1500,
  margin: "0 auto",
  background: "#f5f7fc",
  minHeight: "100vh",
  boxSizing: "border-box",
};

const headerRowStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 10,
  flexWrap: "wrap",
};

const eyebrowStyle = {
  fontSize: 10,
  fontWeight: 900,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: "rgba(15,23,42,0.52)",
  marginBottom: 3,
};

const titleStyle = {
  margin: 0,
  fontSize: 22,
  lineHeight: 1.02,
  fontWeight: 700,
  letterSpacing: "-0.035em",
  color: "rgba(15,23,42,0.94)",
};

const subtitleStyle = {
  margin: "4px 0 0",
  fontSize: 11.5,
  color: "rgba(15,23,42,0.58)",
  lineHeight: 1.32,
};

const controlRowStyle = {
  display: "flex",
  gap: 8,
  alignItems: "center",
  flexWrap: "wrap",
  justifyContent: "flex-end",
};

const smallSelectStyle = {
  height: 34,
  padding: "0 10px",
  borderRadius: 9,
  border: "1px solid rgba(15,23,42,0.10)",
  background: "#ffffff",
  color: "rgba(15,23,42,0.90)",
  fontSize: 12.2,
  fontWeight: 650,
  fontFamily: "inherit",
  minWidth: 180,
  outline: "none",
};

const metricGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(6, minmax(0, 1fr))",
  gap: 8,
};

const bucketRowStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: 10,
  alignItems: "stretch",
};

const bucketSectionStyle = {
  height: "100%",
  display: "flex",
  flexDirection: "column",
};

const bucketBodyStyle = {
  display: "grid",
  gap: 6,
  alignContent: "start",
  maxHeight: 320,
  overflowY: "auto",
  paddingRight: 2,
  scrollbarGutter: "stable",
};

const readyBodyStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: 6,
  alignItems: "start",
};

function metricCardStyle(tone = "neutral") {
  const toneMap = {
    danger: { borderColor: "#fecaca", background: "#fffafa" },
    warning: { borderColor: "#fed7aa", background: "#fff7ed" },
    active: { borderColor: "#bfdbfe", background: "#eff6ff" },
    good: { borderColor: "#bbf7d0", background: "#f0fdf4" },
    neutral: { borderColor: "rgba(15,23,42,0.08)", background: "#ffffff" },
  };
  const picked = toneMap[tone] || toneMap.neutral;

  return {
    background: picked.background,
    border: `1px solid ${picked.borderColor}`,
    borderRadius: 14,
    padding: 11,
    minHeight: 62,
    boxShadow: "0 6px 18px rgba(15,23,42,0.04)",
  };
}

function sectionStyle() {
  return {
    background: "#ffffff",
    border: "1px solid rgba(15,23,42,0.08)",
    borderRadius: 14,
    padding: 11,
    boxShadow: "0 6px 18px rgba(15,23,42,0.04)",
  };
}

function itemCardStyle(alert = false, compact = false) {
  return {
    border: alert ? "1px solid #fecaca" : "1px solid #e5e7eb",
    borderLeft: alert ? "4px solid #dc2626" : "4px solid transparent",
    borderRadius: 11,
    padding: compact ? "6px 8px" : "7px 9px",
    background: alert ? "#fffafa" : "#ffffff",
    boxShadow: "0 3px 10px rgba(15,23,42,0.03)",
  };
}

function MetricCard({ label, value, sub, tone = "neutral" }) {
  return (
    <div style={metricCardStyle(tone)}>
      <div
        style={{
          fontSize: 10.2,
          fontWeight: 850,
          letterSpacing: "0.05em",
          textTransform: "uppercase",
          color: "rgba(15,23,42,0.48)",
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 20,
          fontWeight: 800,
          marginTop: 3,
          lineHeight: 1,
          color: "rgba(15,23,42,0.92)",
          letterSpacing: "-0.025em",
        }}
      >
        {value}
      </div>
      <div
        style={{
          fontSize: 10.8,
          color: "rgba(15,23,42,0.55)",
          marginTop: 5,
          lineHeight: 1.28,
        }}
      >
        {sub}
      </div>
    </div>
  );
}

function Section({ title, hint, children, style }) {
  return (
    <section style={{ ...sectionStyle(), ...style }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 10,
          flexWrap: "wrap",
          alignItems: "baseline",
          marginBottom: 7,
        }}
      >
        <div
          style={{
            fontSize: 10.5,
            fontWeight: 900,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: "rgba(15,23,42,0.72)",
          }}
        >
          {title}
        </div>
        {hint ? (
          <div style={{ fontSize: 11.5, color: "rgba(15,23,42,0.55)" }}>
            {hint}
          </div>
        ) : null}
      </div>
      {children}
    </section>
  );
}

function EmptyState({ text }) {
  return (
    <div
      style={{
        padding: "9px 10px",
        border: "1px dashed #cbd5e1",
        borderRadius: 10,
        color: "#64748b",
        fontSize: 11.5,
      }}
    >
      {text}
    </div>
  );
}

function ROCard({
  ro,
  alert = false,
  footerLabel = "",
  footerAlert = false,
  extraPills = [],
  onOpenThread,
  hasUnreadNote = false,
  compact = false,
}) {
  const tag = getTag(ro);
  const tText = transportLabel(ro?.transportType);
  const tone = transportTone(ro?.transportType);
  const wm = workMixLabel(ro?.workMix);
  const latestNote = latestSharedNote(ro?.notes);

  return (
    <article style={itemCardStyle(alert, compact)}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 8,
          alignItems: "flex-start",
          flexWrap: "nowrap",
        }}
      >
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              display: "flex",
              gap: 6,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <div
              style={{
                fontWeight: 800,
                fontSize: compact ? 12.6 : 13.25,
                lineHeight: 1.1,
                color: "rgba(15,23,42,0.94)",
              }}
            >
              RO #{ro.roNumber || "—"}
            </div>

            <span
              style={tagPillStyle(!!tag)}
              title={tag ? "Key tag number" : "Missing tag number"}
            >
              {tag ? `Tag #${tag}` : "Tag Missing"}
            </span>

            {extraPills.map((pill, idx) => (
              <span key={`${pill.label}-${idx}`} style={pill.style}>
                {pill.label}
              </span>
            ))}

            {tText ? <span style={tonePillStyle(tone)}>{tText}</span> : null}
            <span style={tonePillStyle("other")}>{wm}</span>
          </div>

          <div
            style={{
              marginTop: compact ? 3 : 4,
              fontSize: compact ? 11.1 : 11.5,
              color: "rgba(15,23,42,0.74)",
            }}
          >
            <b>{ro.customerName || "Customer"}</b> — {ro.vehicle || "—"}
          </div>

          <div
            style={{
              marginTop: compact ? 3 : 4,
              display: "flex",
              flexWrap: "wrap",
              gap: "5px 11px",
              fontSize: 10.8,
              color: "rgba(15,23,42,0.55)",
            }}
          >
            {ro.assignedTechName ? (
              <div>
                Tech: <b style={{ color: "#374151" }}>{ro.assignedTechName}</b>
              </div>
            ) : null}

            {ro.promiseTimeISO ? (
              <div>
                Promise: <b style={{ color: "#374151" }}>{fmtPromise(ro.promiseTimeISO)}</b>
              </div>
            ) : null}
          </div>

          {latestNote ? (
            <div
              style={{
                marginTop: compact ? 4 : 5,
                fontSize: 10.8,
                color: "#6b7280",
                background: "#f8fafc",
                border: "1px solid #e5e7eb",
                borderRadius: 9,
                padding: compact ? "4px 6px" : "5px 7px",
                ...(compact
                  ? {
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }
                  : {}),
              }}
            >
              <span style={{ fontWeight: 900, color: "#4b5563" }}>Latest note:</span>{" "}
              {latestNote}
            </div>
          ) : null}
        </div>

        <div
          style={{
            whiteSpace: "nowrap",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 6,
            flexWrap: "wrap",
          }}
        >
          {footerLabel ? (
            <span style={tonePillStyle(footerAlert ? "stage" : "other")}>
              {footerLabel}
            </span>
          ) : null}

          {typeof onOpenThread === "function" ? (
            <button
              type="button"
              onClick={() => onOpenThread(ro)}
              style={threadButtonStyle(hasUnreadNote)}
              title={hasUnreadNote ? "Unread note activity" : "Open notes and activity"}
            >
              {compact ? "Thread" : "Notes / Activity"}
              {hasUnreadNote ? (
                <span
                  style={{
                    position: "absolute",
                    top: -5,
                    right: -5,
                    width: 10,
                    height: 10,
                    borderRadius: 999,
                    background: "#f97316",
                    border: "2px solid #ffffff",
                    boxShadow: "0 0 0 1px rgba(249,115,22,0.18)",
                  }}
                />
              ) : null}
            </button>
          ) : null}
        </div>
      </div>
    </article>
  );
}

export default function ShopForemanDashboard() {
  const store = useROStore();
  const auth = useAuth();
  const {
    hasUnread,
    isHydrated: unreadHydrated,
    storeId: unreadStoreId,
  } = useROUnread();
  const ros = normalizeROArray(store);

  const currentUser = store?.currentUser || {};

  const actorRole = safeString(currentUser?.role || "foreman");
  const actorName = safeString(currentUser?.name || "Shop Foreman");

  const [threadROId, setThreadROId] = useState(null);
  const currentUnreadStoreId = safeString(
    auth?.effectiveStoreId || auth?.activeStoreId
  ).trim();
  const unreadStoreMatches =
    !!currentUnreadStoreId &&
    safeString(unreadStoreId).trim() === currentUnreadStoreId;
  const [dateFilter, setDateFilter] = useState(DATE_FILTERS.TODAY);
  const [customFromDate, setCustomFromDate] = useState(() => dateInputValue());
  const [customToDate, setCustomToDate] = useState(() => dateInputValue());

  const dateWindow = useMemo(
    () => getForemanDateWindow(dateFilter, customFromDate, customToDate),
    [dateFilter, customFromDate, customToDate]
  );

  const RO_STATUS_KEYS = useMemo(() => {
    const src = store?.RO_STATUS || FALLBACK_RO_STATUS;
    return {
      DISPATCH: String(src?.DISPATCH || "DISPATCH").toUpperCase(),
      ASSIGNED: String(src?.ASSIGNED || "ASSIGNED").toUpperCase(),
      IN_PROGRESS: String(src?.IN_PROGRESS || "IN_PROGRESS").toUpperCase(),
      WAITING_ON_PARTS: String(
        src?.WAITING_ON_PARTS || "WAITING_ON_PARTS"
      ).toUpperCase(),
      COMPLETED: String(src?.COMPLETED || "COMPLETED").toUpperCase(),
      ARCHIVED: String(src?.ARCHIVED || "ARCHIVED").toUpperCase(),
    };
  }, [store?.RO_STATUS]);

  const RO_LIFECYCLE_KEYS = useMemo(() => {
    const src = store?.RO_LIFECYCLE || FALLBACK_RO_LIFECYCLE;
    return {
      OPEN: String(src?.OPEN || "OPEN").toUpperCase(),
      WAITING_FOR_PARTS: String(
        src?.WAITING_FOR_PARTS || "WAITING_FOR_PARTS"
      ).toUpperCase(),
      WAITING_FOR_ASSIGNMENT: String(
        src?.WAITING_FOR_ASSIGNMENT || "WAITING_FOR_ASSIGNMENT"
      ).toUpperCase(),
      IN_PROGRESS: String(src?.IN_PROGRESS || "IN_PROGRESS").toUpperCase(),
      COMPLETED: String(src?.COMPLETED || "COMPLETED").toUpperCase(),
    };
  }, [store?.RO_LIFECYCLE]);

  const enums = useMemo(
    () => ({
      RO_STATUS: RO_STATUS_KEYS,
      RO_LIFECYCLE: RO_LIFECYCLE_KEYS,
    }),
    [RO_STATUS_KEYS, RO_LIFECYCLE_KEYS]
  );

  const activeROs = useMemo(() => {
    const source =
      Array.isArray(store?.wipROs) && store.wipROs.length > 0
        ? store.wipROs
        : ros;

    return source.filter(
      (ro) =>
        !isArchivedLike(ro, RO_STATUS_KEYS) &&
        !isCompletedLike(ro, RO_STATUS_KEYS, RO_LIFECYCLE_KEYS) &&
        !isVehicleGoneLike(ro)
    );
  }, [store?.wipROs, ros, RO_STATUS_KEYS, RO_LIFECYCLE_KEYS]);

  const dateScopedActiveROs = useMemo(() => {
    return activeROs.filter((ro) => isWithinForemanDateWindow(ro, dateWindow));
  }, [activeROs, dateWindow]);

  const threadRO = useMemo(() => {
    if (!threadROId) return null;

    return (
      activeROs.find((ro) => String(ro?.id) === String(threadROId)) ||
      ros.find((ro) => String(ro?.id) === String(threadROId)) ||
      null
    );
  }, [threadROId, activeROs, ros]);

  const hasUnreadThreadNote = useCallback(
    (ro) => {
      const roId = safeString(ro?.id).trim();
      return !!(unreadHydrated && unreadStoreMatches && roId && hasUnread(roId));
    },
    [hasUnread, unreadHydrated, unreadStoreMatches]
  );

  const openThreadForRo = useCallback(
    (ro) => {
      const roId = safeString(ro?.id).trim();
      if (!roId) return;

      setThreadROId(roId);
    },
    []
  );

  const closeThread = useCallback(() => {
    setThreadROId(null);
  }, []);

  const inDispatchQueue = useMemo(() => {
    return dateScopedActiveROs.filter((ro) => {
      const lc = lifecycleOf(ro);
      if (lc) return lc === RO_LIFECYCLE_KEYS.WAITING_FOR_ASSIGNMENT;

      const st = statusOf(ro);
      const looksOpen = st === RO_STATUS_KEYS.DISPATCH || st === "OPEN";
      return looksOpen && !isAssignedLike(ro);
    });
  }, [dateScopedActiveROs, RO_LIFECYCLE_KEYS, RO_STATUS_KEYS]);

  const assigned = useMemo(() => {
    return dateScopedActiveROs.filter((ro) => {
      const lc = lifecycleOf(ro);
      if (lc) return lc === RO_LIFECYCLE_KEYS.OPEN && isAssignedLike(ro);
      return statusOf(ro) === RO_STATUS_KEYS.ASSIGNED;
    });
  }, [dateScopedActiveROs, RO_LIFECYCLE_KEYS, RO_STATUS_KEYS]);

  const inProgress = useMemo(() => {
    return dateScopedActiveROs.filter((ro) => {
      const lc = lifecycleOf(ro);
      if (lc) return lc === RO_LIFECYCLE_KEYS.IN_PROGRESS;
      return statusOf(ro) === RO_STATUS_KEYS.IN_PROGRESS;
    });
  }, [dateScopedActiveROs, RO_LIFECYCLE_KEYS, RO_STATUS_KEYS]);

  const waitingOnParts = useMemo(() => {
    return dateScopedActiveROs.filter((ro) => isWaitingOnPartsLike(ro, enums));
  }, [dateScopedActiveROs, enums]);

  const partsReadyNotStarted = useMemo(() => {
    return dateScopedActiveROs.filter((ro) => {
      const lc = lifecycleOf(ro);
      const assignedBucket = lc
        ? lc === RO_LIFECYCLE_KEYS.OPEN && isAssignedLike(ro)
        : statusOf(ro) === RO_STATUS_KEYS.ASSIGNED;

      if (!assignedBucket) return false;

      const ps = String(ro?.partsStatus || "").toUpperCase();
      return ps === "ARRIVED" || ps === "PULLED";
    });
  }, [dateScopedActiveROs, RO_LIFECYCLE_KEYS, RO_STATUS_KEYS]);

  const stuckJobs = useMemo(() => {
    const nowIso = new Date().toISOString();
    const stuck = [];

    inDispatchQueue.forEach((ro) => {
      const start = getCreatedISO(ro) || ro?.timestamps?.createdAt || ro?.createdAt;
      const mins = minutesSince(start, nowIso);
      if (mins != null && mins >= DISPATCH_WARN_MIN) {
        stuck.push({ ro, stage: "Dispatch Queue", minutes: mins });
      }
    });

    waitingOnParts.forEach((ro) => {
      const start =
        safeISO(ro?.timestamps?.waitingPartsAt) ||
        safeISO(ro?.waitingPartsAt) ||
        safeISO(ro?.partsActionRequestedAt) ||
        safeISO(getCreatedISO(ro)) ||
        safeISO(ro?.createdAt);

      const mins = minutesSince(start, nowIso);
      if (mins != null && mins >= PARTS_WARN_MIN) {
        stuck.push({ ro, stage: "Waiting on Parts", minutes: mins });
      }
    });

    return stuck.sort((a, b) => b.minutes - a.minutes);
  }, [inDispatchQueue, waitingOnParts]);

  const watchListPreview = useMemo(() => stuckJobs.slice(0, 4), [stuckJobs]);
  const watchListTitle =
    stuckJobs.length > watchListPreview.length
      ? `Watch List (${watchListPreview.length} of ${stuckJobs.length})`
      : `Watch List (${stuckJobs.length})`;

  const totalActive = dateScopedActiveROs.length;

  const renderROCard = useCallback(
    (props) => {
      const ro = props?.ro;
      return (
        <ROCard
          {...props}
          onOpenThread={openThreadForRo}
          hasUnreadNote={hasUnreadThreadNote(ro)}
        />
      );
    },
    [openThreadForRo, hasUnreadThreadNote]
  );

  return (
    <div className="shop-foreman-page" style={pageStyle}>
      <div className="shop-foreman-header" style={headerRowStyle}>
        <div className="shop-foreman-title-block" style={{ minWidth: 280 }}>
          <div style={eyebrowStyle}>Shop Foreman</div>
          <h1 style={titleStyle}>Shop Foreman Dashboard</h1>
          <div style={subtitleStyle}>
            Live view of shop flow, technician handoffs, parts delays, and active bottlenecks.
          </div>
        </div>

        <div className="shop-foreman-controls" style={controlRowStyle}>
          <select
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            style={smallSelectStyle}
            aria-label="Foreman date range"
          >
            <option value={DATE_FILTERS.TODAY}>Date: Today</option>
            <option value={DATE_FILTERS.YESTERDAY}>Date: Yesterday</option>
            <option value={DATE_FILTERS.THIS_WEEK}>Date: This Week</option>
            <option value={DATE_FILTERS.THIS_MONTH}>Date: This Month</option>
            <option value={DATE_FILTERS.CUSTOM}>Date: Custom</option>
          </select>

          {dateFilter === DATE_FILTERS.CUSTOM ? (
            <>
              <input
                type="date"
                value={customFromDate}
                onChange={(e) => setCustomFromDate(e.target.value)}
                style={{ ...smallSelectStyle, minWidth: 145 }}
              />
              <span style={{ fontSize: 11.5, color: "#64748b", fontWeight: 900 }}>
                to
              </span>
              <input
                type="date"
                value={customToDate}
                onChange={(e) => setCustomToDate(e.target.value)}
                style={{ ...smallSelectStyle, minWidth: 145 }}
              />
            </>
          ) : null}

          <span style={tonePillStyle(totalActive > 0 ? "active" : "good")}>
            Showing: {totalActive}
          </span>
        </div>
      </div>

      <div className="shop-foreman-metrics" style={metricGridStyle}>
        <MetricCard
          label="Active ROs"
          value={totalActive}
          sub={`Visible for ${dateWindow.label}.`}
          tone={totalActive > 0 ? "active" : "good"}
        />
        <MetricCard
          label="Unassigned"
          value={inDispatchQueue.length}
          sub="Needs dispatch."
          tone={inDispatchQueue.length > 0 ? "danger" : "good"}
        />
        <MetricCard
          label="Assigned"
          value={assigned.length}
          sub="Not started."
          tone={assigned.length > 0 ? "neutral" : "good"}
        />
        <MetricCard
          label="In Progress"
          value={inProgress.length}
          sub="Techs working."
          tone={inProgress.length > 0 ? "active" : "good"}
        />
        <MetricCard
          label="Waiting Parts"
          value={waitingOnParts.length}
          sub="Parts delay."
          tone={waitingOnParts.length > 0 ? "warning" : "good"}
        />
        <MetricCard
          label="Watch List"
          value={stuckJobs.length}
          sub="Needs follow-up."
          tone={stuckJobs.length > 0 ? "danger" : "good"}
        />
      </div>

      <Section title={watchListTitle} hint="Top bottlenecks. Buckets below show the rest.">
        {stuckJobs.length === 0 ? (
          <EmptyState text="No jobs over thresholds. Nice work." />
        ) : (
          <div style={{ display: "grid", gap: 6 }}>
            {watchListPreview.map(({ ro, stage, minutes }) =>
              renderROCard({
                key: ro.id || ro.roNumber,
                ro,
                alert: true,
                footerLabel: formatMinutesCompact(minutes),
                footerAlert: true,
                extraPills: [
                  {
                    label: stage,
                    style: tonePillStyle("stage"),
                  },
                  ...(ro.assignedTechName
                    ? [
                        {
                          label: ro.assignedTechName,
                          style: tonePillStyle("tech"),
                        },
                      ]
                    : []),
                ],
              })
            )}
          </div>
        )}
      </Section>

      <div className="shop-foreman-bucket-row" style={bucketRowStyle}>
        <Section
          title={`Dispatch Queue (${inDispatchQueue.length})`}
          hint="Unassigned jobs. Oldest first."
          style={bucketSectionStyle}
        >
          {inDispatchQueue.length === 0 ? (
            <EmptyState text="No ROs currently in dispatch queue." />
          ) : (
            <div style={bucketBodyStyle}>
              {inDispatchQueue
                .slice()
                .sort((a, b) => {
                  const aM = minutesSince(getCreatedISO(a) || a?.createdAt);
                  const bM = minutesSince(getCreatedISO(b) || b?.createdAt);
                  return (bM || 0) - (aM || 0);
                })
                .map((ro) => {
                  const mins = minutesSince(getCreatedISO(ro) || ro?.createdAt);
                  const isLate = mins != null && mins >= DISPATCH_WARN_MIN;

                  return renderROCard({
                    key: ro.id || ro.roNumber,
                    ro,
                    compact: true,
                    alert: isLate,
                    footerLabel: `Age ${formatMinutesCompact(mins)}`,
                    footerAlert: isLate,
                    extraPills: [
                      {
                        label: "Unassigned",
                        style: tonePillStyle("queue"),
                      },
                    ],
                  });
                })}
            </div>
          )}
        </Section>

        <Section
          title={`Assigned (${assigned.length})`}
          hint="Assigned to a tech, waiting to be started."
          style={bucketSectionStyle}
        >
          {assigned.length === 0 ? (
            <EmptyState text="No assigned jobs waiting to be started." />
          ) : (
            <div style={bucketBodyStyle}>
              {assigned
                .slice()
                .sort((a, b) => {
                  const aStart = getAssignedISO(a) || getCreatedISO(a) || a?.createdAt;
                  const bStart = getAssignedISO(b) || getCreatedISO(b) || b?.createdAt;
                  const aM = minutesSince(aStart);
                  const bM = minutesSince(bStart);
                  return (bM || 0) - (aM || 0);
                })
                .map((ro) =>
                  renderROCard({
                    key: ro.id || ro.roNumber,
                    ro,
                    compact: true,
                    extraPills: [
                      {
                        label: "Assigned",
                        style: tonePillStyle("assigned"),
                      },
                      ...(ro.assignedTechName
                        ? [
                            {
                              label: ro.assignedTechName,
                              style: tonePillStyle("tech"),
                            },
                          ]
                        : []),
                    ],
                  })
                )}
            </div>
          )}
        </Section>
      </div>

      <div className="shop-foreman-bucket-row" style={bucketRowStyle}>
        <Section
          title={`In Progress (${inProgress.length})`}
          hint="Tech has started work."
          style={bucketSectionStyle}
        >
          {inProgress.length === 0 ? (
            <EmptyState text="No jobs currently in progress." />
          ) : (
            <div style={bucketBodyStyle}>
              {inProgress
                .slice()
                .sort((a, b) => {
                  const aT = new Date(
                    a?.timestamps?.workStartAt || a?.workStartAt || 0
                  ).getTime();
                  const bT = new Date(
                    b?.timestamps?.workStartAt || b?.workStartAt || 0
                  ).getTime();
                  return aT - bT;
                })
                .map((ro) =>
                  renderROCard({
                    key: ro.id || ro.roNumber,
                    ro,
                    compact: true,
                    extraPills: [
                      {
                        label: "In Progress",
                        style: tonePillStyle("inprogress"),
                      },
                      ...(ro.assignedTechName
                        ? [
                            {
                              label: ro.assignedTechName,
                              style: tonePillStyle("tech"),
                            },
                          ]
                        : []),
                    ],
                  })
                )}
            </div>
          )}
        </Section>

        <Section
          title={`Waiting Parts (${waitingOnParts.length})`}
          hint="Long waits may need escalation."
          style={bucketSectionStyle}
        >
          {waitingOnParts.length === 0 ? (
            <EmptyState text="No ROs currently waiting on parts." />
          ) : (
            <div style={bucketBodyStyle}>
              {waitingOnParts
                .slice()
                .sort((a, b) => {
                  const aStart =
                    safeISO(a?.timestamps?.waitingPartsAt) ||
                    safeISO(a?.waitingPartsAt) ||
                    safeISO(a?.partsActionRequestedAt) ||
                    safeISO(getCreatedISO(a)) ||
                    safeISO(a?.createdAt);

                  const bStart =
                    safeISO(b?.timestamps?.waitingPartsAt) ||
                    safeISO(b?.waitingPartsAt) ||
                    safeISO(b?.partsActionRequestedAt) ||
                    safeISO(getCreatedISO(b)) ||
                    safeISO(b?.createdAt);

                  const aM = minutesSince(aStart);
                  const bM = minutesSince(bStart);
                  return (bM || 0) - (aM || 0);
                })
                .map((ro) => {
                  const start =
                    safeISO(ro?.timestamps?.waitingPartsAt) ||
                    safeISO(ro?.waitingPartsAt) ||
                    safeISO(ro?.partsActionRequestedAt) ||
                    safeISO(getCreatedISO(ro)) ||
                    safeISO(ro?.createdAt);

                  const mins = minutesSince(start);
                  const isLate = mins != null && mins >= PARTS_WARN_MIN;

                  return renderROCard({
                    key: ro.id || ro.roNumber,
                    ro,
                    compact: true,
                    alert: isLate,
                    footerLabel: `Waiting ${formatMinutesCompact(mins)}`,
                    footerAlert: isLate,
                    extraPills: [
                      {
                        label: "Waiting Parts",
                        style: tonePillStyle("parts"),
                      },
                      ...(ro.assignedTechName
                        ? [
                            {
                              label: ro.assignedTechName,
                              style: tonePillStyle("tech"),
                            },
                          ]
                        : []),
                    ],
                  });
                })}
            </div>
          )}
        </Section>
      </div>

      <Section
        title={`Parts Ready, Not Started (${partsReadyNotStarted.length})`}
        hint="Parts arrived or pulled, but work hasn’t begun."
      >
        {partsReadyNotStarted.length === 0 ? (
          <EmptyState text="No ROs in this category." />
        ) : (
          <div className="shop-foreman-ready-grid" style={readyBodyStyle}>
            {partsReadyNotStarted.map((ro) => {
              const ps = String(ro?.partsStatus || "").toUpperCase();
              return renderROCard({
                key: ro.id || ro.roNumber,
                ro,
                compact: true,
                extraPills: [
                  {
                    label: `Parts ${ps === "ARRIVED" ? "Arrived" : "Pulled"}`,
                    style: tonePillStyle("parts"),
                  },
                  ...(ro.assignedTechName
                    ? [
                        {
                          label: ro.assignedTechName,
                          style: tonePillStyle("tech"),
                        },
                      ]
                    : []),
                ],
              });
            })}
          </div>
        )}
      </Section>

      <style>
        {`
          @media (max-width: 1320px) {
            .shop-foreman-metrics {
              grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
            }
          }

          @media (max-width: 1180px) {
            .shop-foreman-page {
              padding: 8px !important;
            }

            .shop-foreman-header {
              align-items: flex-start !important;
            }

            .shop-foreman-title-block {
              min-width: 0 !important;
            }

            .shop-foreman-controls {
              width: 100%;
              justify-content: flex-start !important;
            }

            .shop-foreman-controls select,
            .shop-foreman-controls input {
              min-width: 0 !important;
              flex: 1 1 170px;
            }

            .shop-foreman-bucket-row {
              grid-template-columns: 1fr !important;
            }

            .shop-foreman-ready-grid {
              grid-template-columns: 1fr !important;
            }
          }

          @media (max-width: 900px) {
            .shop-foreman-page {
              padding: 4px !important;
              gap: 8px !important;
            }

            .shop-foreman-metrics {
              grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            }
          }

          @media (max-width: 560px) {
            .shop-foreman-metrics {
              grid-template-columns: 1fr !important;
            }
          }
        `}
      </style>

      <ROActivityDrawer
        ro={threadRO}
        isOpen={!!threadRO}
        onClose={closeThread}
        currentUser={currentUser}
        actorRole={actorRole}
        actorName={actorName}
        title="Shop Foreman RO Thread"
      />
    </div>
  );
}
